import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Shield, Monitor, Bell, Zap, Users, HeadphonesIcon, BarChart3, Clock, Lock, Globe, FileText, Settings, ArrowLeft } from 'lucide-react';

const FeaturesPage = () => {
  const navigate = useNavigate();

  const featureCategories = [
    {
      category: 'Remote Monitoring & Management',
      icon: Monitor,
      features: [
        { name: 'Real-time Endpoint Monitoring', desc: 'Monitor CPU, memory, disk usage, and network performance in real-time' },
        { name: 'Automated Health Checks', desc: 'Proactive system health monitoring with intelligent alerting' },
        { name: 'Screenshot Capture', desc: 'Automatic screenshot recording for troubleshooting and compliance' },
        { name: 'Remote Control', desc: 'Integrated ScreenConnect for instant remote access' },
        { name: 'Patch Management', desc: 'Automated Windows updates and third-party patch deployment' },
        { name: 'Script Automation', desc: 'Deploy and execute PowerShell scripts across your fleet' }
      ]
    },
    {
      category: 'Help Desk & Ticketing',
      icon: HeadphonesIcon,
      features: [
        { name: 'Advanced Ticketing System', desc: 'Full-featured ticket management with automation workflows' },
        { name: 'Client Portal', desc: 'Self-service portal for clients to submit and track tickets' },
        { name: 'SLA Management', desc: 'Track and enforce service level agreements automatically' },
        { name: 'Knowledge Base', desc: 'Built-in knowledge base for common issues and solutions' },
        { name: 'Email Integration', desc: 'Create tickets directly from emails' },
        { name: 'Mobile App', desc: 'Manage tickets on the go with our mobile applications' }
      ]
    },
    {
      category: 'PSA & Project Management',
      icon: BarChart3,
      features: [
        { name: 'Time Tracking', desc: 'Track billable hours and generate accurate invoices' },
        { name: 'Project Management', desc: 'Manage projects with tasks, milestones, and dependencies' },
        { name: 'Billing & Invoicing', desc: 'Automated billing with multiple pricing models' },
        { name: 'Contract Management', desc: 'Manage service agreements and recurring revenue' },
        { name: 'Resource Planning', desc: 'Optimize technician schedules and workload' },
        { name: 'Financial Reporting', desc: 'Comprehensive financial analytics and forecasting' }
      ]
    },
    {
      category: 'CRM & Client Management',
      icon: Users,
      features: [
        { name: 'Contact Management', desc: 'Centralized database of all clients and contacts' },
        { name: 'Client Types', desc: 'Organize clients by type, industry, and service level' },
        { name: 'Document Storage', desc: 'Secure storage for contracts, credentials, and documentation' },
        { name: 'Communication History', desc: 'Track all interactions and communications with clients' },
        { name: 'Sales Pipeline', desc: 'Manage leads and opportunities through the sales funnel' },
        { name: 'Custom Fields', desc: 'Add custom data fields to match your business needs' }
      ]
    },
    {
      category: 'Security & Compliance',
      icon: Lock,
      features: [
        { name: 'Security Scoring', desc: 'Automated security health scoring for all endpoints' },
        { name: 'Antivirus Monitoring', desc: 'Track AV status and definitions across all systems' },
        { name: 'Firewall Management', desc: 'Monitor and configure Windows Firewall remotely' },
        { name: 'Compliance Reports', desc: 'Generate compliance reports for various standards' },
        { name: 'Audit Logging', desc: 'Complete audit trail of all system changes and access' },
        { name: 'Multi-Factor Authentication', desc: 'Secure access with 2FA for all users' }
      ]
    },
    {
      category: 'Automation & Integration',
      icon: Zap,
      features: [
        { name: 'Workflow Automation', desc: 'Create custom workflows to automate repetitive tasks' },
        { name: 'Alert Automation', desc: 'Smart alerting with customizable thresholds and actions' },
        { name: 'API Access', desc: 'RESTful API for custom integrations and development' },
        { name: 'Third-party Integrations', desc: 'Connect with popular tools like Slack, Teams, and more' },
        { name: 'Webhook Support', desc: 'Real-time event notifications via webhooks' },
        { name: 'Custom Scripts', desc: 'Schedule and deploy custom PowerShell scripts' }
      ]
    },
    {
      category: 'Reporting & Analytics',
      icon: FileText,
      features: [
        { name: 'Real-time Dashboards', desc: 'Customizable dashboards with real-time metrics' },
        { name: 'Executive Reports', desc: 'High-level reports for C-level presentations' },
        { name: 'Performance Analytics', desc: 'Deep insights into system and business performance' },
        { name: 'Custom Reports', desc: 'Build custom reports with our report builder' },
        { name: 'Scheduled Reports', desc: 'Automatically email reports on a schedule' },
        { name: 'Export Options', desc: 'Export data in PDF, Excel, and CSV formats' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="mr-2">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800">RMM Pro</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={() => navigate('/login')}>
                Login
              </Button>
              <Button onClick={() => navigate('/signup')} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Comprehensive Features</h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Everything you need to manage, monitor, and grow your MSP business in one unified platform
          </p>
        </div>
      </section>

      {/* Features List */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {featureCategories.map((category, catIndex) => (
              <div key={catIndex}>
                <div className="flex items-center mb-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mr-4">
                    <category.icon className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900">{category.category}</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.features.map((feature, featureIndex) => (
                    <Card key={featureIndex} className="border-slate-200 hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.name}</h3>
                        <p className="text-slate-600">{feature.desc}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to See It in Action?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Start your 14-day free trial today - no credit card required
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/signup')}
            className="bg-white text-blue-600 hover:bg-slate-100 text-lg px-8"
          >
            Start Free Trial
          </Button>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage;